// src/components/Account/AccountDetails.tsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Alert,
  Tooltip,
  Typography,
  TextField,
  Autocomplete,
  Avatar,
  AvatarGroup,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  LinearProgress,
  Stack,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  useTheme,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CommentIcon from "@mui/icons-material/Comment";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import BusinessIcon from "@mui/icons-material/Business";
import PublicIcon from "@mui/icons-material/Public";
import LanguageIcon from "@mui/icons-material/Language";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import EmailIcon from "@mui/icons-material/Email";
import PlaceIcon from "@mui/icons-material/Place";
import FolderIcon from "@mui/icons-material/Folder";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TimelineIcon from "@mui/icons-material/Timeline";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";

import { baseUrl } from "../../constants";
import { TaskStatus } from "../../enums/task-status.enum";
import { TaskImportance } from "../../enums/task-importance.enum";
import { TaskRemindIn } from "../../enums/task_remind_in.enum";
import {
  getTaskStatusContainerBgColor,
  getTaskStatusContainerTextColor,
  getTaskStatusLabel,
} from "../../utils/task-status.utils";
import {
  getImportanceContainerColor,
  getImportanceContainerText,
} from "../../utils/task-importance.utils";
import { handleDateWithTime } from "../../utils/date-handlers";

/* ===================== Types ===================== */
type Dept = { id: number; name: string };
type ProjectLite = { id: number; name: string; department?: Dept | null };

type UserLite = {
  id: number;
  username: string;
  profile?: { imageUrl?: string | null } | null;
};

type AssignedUserLink = { user: UserLite };

type TaskLite = {
  id: number;
  title: string;
  description?: string | null;
  ref?: string | null;
  status: TaskStatus;
  importance: TaskImportance;
  endDate: string;
  startDate: string;
  remindIn?: TaskRemindIn | null;
  remindAt?: string | null;
  reminded: boolean;
  project: ProjectLite | null;
  assignedUsers?: AssignedUserLink[];
  _count?: { taskComments: number; taskAttachments: number };
};

type AccountDetailsResponse = {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  notes?: string | null;

  type: string;
  status: string;
  visibility: string;
  tags: string[];

  email?: string | null;
  phone?: string | null;
  website?: string | null;
  domain?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;

  workspaceId: number;
  workspace?: { id: number; name: string };

  linkedUserId?: number | null;

  tasks: TaskLite[];
};

/* ===================== Component ===================== */
const AccountDetails: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { accountId } = useParams<{ accountId: string }>();
  const token = useMemo(
    () => localStorage.getItem("accessToken") || localStorage.getItem("token"),
    []
  );

  // Tabs & timeframe for charts
  const [tab, setTab] = useState(0);
  const [timeframe, setTimeframe] = useState<"12m" | "all">("12m");

  // Loading & data
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState<AccountDetailsResponse | null>(null);

  // UI & feedback
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "success" });

  const [chatLoading, setChatLoading] = useState<boolean>(false);
  const [infoOpen, setInfoOpen] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [selectedProjects, setSelectedProjects] = useState<ProjectLite[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<Dept[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<TaskStatus[]>([]);
  const [selectedImportances, setSelectedImportances] = useState<
    TaskImportance[]
  >([]);

  // Expand/collapse
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  // Pagination (per-project groups)
  const PAGE_SIZE = 50;
  const [page, setPage] = useState<number>(1);

  // Abort controller for fetch
  const abortRef = useRef<AbortController | null>(null);

  /* ============== Fetch (with cancel-safe handling) ============== */
  const fetchAccount = useCallback(async () => {
    if (!accountId) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    try {
      const { data } = await axios.get<AccountDetailsResponse>(
        `${baseUrl}/accounts/by-id/${accountId}`,
        { headers: { Authorization: `Bearer ${token}` }, signal: ctrl.signal }
      );

      setAccount(data);

      // Expand all project groups by default for quick overview
      const allIds = new Set<number>();
      (data.tasks || []).forEach((t) => allIds.add(t.project?.id ?? -1));
      setExpandedIds(allIds);
    } catch (err: any) {
      if (axios.isCancel(err)) return; // <- ignore noisy canceled fetch
      console.error(err);
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message || "Failed to load account details.",
        severity: "error",
      });
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, [accountId, token]);

  useEffect(() => {
    fetchAccount();
    return () => abortRef.current?.abort();
  }, [fetchAccount]);

  /* ===================== Options for filters ===================== */
  const projectOptions: ProjectLite[] = useMemo(() => {
    const map = new Map<number, ProjectLite>();
    (account?.tasks || []).forEach((t) => {
      if (t.project) map.set(t.project.id, t.project);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [account]);

  const deptOptions: Dept[] = useMemo(() => {
    const map = new Map<number, Dept>();
    (account?.tasks || []).forEach((t) => {
      const dep = t.project?.department;
      if (dep) map.set(dep.id, dep);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [account]);

  const statusOptions: TaskStatus[] = useMemo(
    () =>
      Object.values(TaskStatus).filter(
        (s) => String(s).toLowerCase() !== "all"
      ) as TaskStatus[],
    []
  );
  const importanceOptions: TaskImportance[] = useMemo(
    () => Object.values(TaskImportance) as TaskImportance[],
    []
  );

  /* ===================== Filtered tasks ===================== */
  const filteredTasks = useMemo(() => {
    const lower = search.trim().toLowerCase();
    return (account?.tasks || []).filter((t) => {
      if (lower) {
        const hay = `${t.title || ""} ${t.description || ""} ${
          t.ref || ""
        }`.toLowerCase();
        if (!hay.includes(lower)) return false;
      }
      if (selectedProjects.length) {
        const pid = t.project?.id ?? -1;
        if (!selectedProjects.some((p) => p.id === pid)) return false;
      }
      if (selectedDepartments.length) {
        const did = t.project?.department?.id ?? -1;
        if (!selectedDepartments.some((d) => d.id === did)) return false;
      }
      if (selectedStatuses.length && !selectedStatuses.includes(t.status)) {
        return false;
      }
      if (
        selectedImportances.length &&
        !selectedImportances.includes(t.importance)
      ) {
        return false;
      }
      return true;
    });
  }, [
    account,
    search,
    selectedProjects,
    selectedDepartments,
    selectedStatuses,
    selectedImportances,
  ]);

  /* ===================== Group tasks by project ===================== */
  const projectGroups = useMemo(() => {
    const map = new Map<
      number,
      {
        projectId: number;
        projectName: string;
        departmentName: string | null;
        tasks: TaskLite[];
        assignees: UserLite[]; // unique across tasks
      }
    >();

    filteredTasks.forEach((t) => {
      const pid = t.project?.id ?? -1;
      const entry = map.get(pid) || {
        projectId: pid,
        projectName: t.project?.name ?? "Unknown Project",
        departmentName: t.project?.department?.name ?? null,
        tasks: [],
        assignees: [],
      };

      entry.tasks.push(t);

      // collect unique assignees for the project header
      (t.assignedUsers || []).forEach((au) => {
        const u = au.user;
        if (!entry.assignees.some((x) => x.id === u.id))
          entry.assignees.push(u);
      });

      map.set(pid, entry);
    });

    return Array.from(map.values()).sort((a, b) =>
      a.projectName.localeCompare(b.projectName)
    );
  }, [filteredTasks]);

  // Reset pagination when filters/search/tab change
  useEffect(() => {
    setPage(1);
  }, [
    search,
    selectedProjects,
    selectedDepartments,
    selectedStatuses,
    selectedImportances,
    tab,
  ]);

  // Pagination slice
  const totalPages = Math.max(1, Math.ceil(projectGroups.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const endIdx = Math.min(startIdx + PAGE_SIZE, projectGroups.length);
  const pagedProjectGroups = projectGroups.slice(startIdx, endIdx);

  /* ===================== Analytics (Overview) ===================== */
  const statusChartData = useMemo(() => {
    const by: Record<string, number> = {};
    (account?.tasks || []).forEach((t) => {
      const key = getTaskStatusLabel(t.status);
      by[key] = (by[key] || 0) + 1;
    });
    return Object.entries(by).map(([name, value]) => ({ name, value }));
  }, [account]);

  const importanceChartData = useMemo(() => {
    const by: Record<string, number> = {};
    (account?.tasks || []).forEach((t) => {
      const key = getImportanceContainerText(t.importance);
      by[key] = (by[key] || 0) + 1;
    });
    const ordered = ["Low", "Medium", "High", "Critical"];
    const mapped = Object.entries(by).map(([name, value]) => ({ name, value }));
    return mapped.sort(
      (a, b) =>
        (ordered.indexOf(a.name) === -1 ? 999 : ordered.indexOf(a.name)) -
        (ordered.indexOf(b.name) === -1 ? 999 : ordered.indexOf(b.name))
    );
  }, [account]);

  const departmentChartData = useMemo(() => {
    const by: Record<string, number> = {};
    (account?.tasks || []).forEach((t) => {
      const name = t.project?.department?.name || "—";
      by[name] = (by[name] || 0) + 1;
    });
    return Object.entries(by).map(([name, value]) => ({ name, value }));
  }, [account]);

  const monthlyChartData = useMemo(() => {
    const by: Record<string, { month: string; total: number; done: number }> =
      {};
    (account?.tasks || []).forEach((t) => {
      const dt = new Date(t.endDate);
      if (Number.isNaN(dt.getTime())) return;
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      if (!by[key]) by[key] = { month: key, total: 0, done: 0 };
      by[key].total += 1;
      const st = String(t.status).toUpperCase();
      if (st.includes("DONE") || st.includes("COMPLETE")) by[key].done += 1;
    });
    return Object.values(by).sort((a, b) => a.month.localeCompare(b.month));
  }, [account]);

  // ✅ filter AFTER monthlyChartData is defined (fixes "used before declared")
  const monthlyChartDataFiltered = useMemo(
    () =>
      timeframe === "all" ? monthlyChartData : monthlyChartData.slice(-12),
    [timeframe, monthlyChartData]
  );

  // Summary stats
  const stats = useMemo(() => {
    const all = account?.tasks || [];
    const total = all.length;
    const done = all.filter((t) => {
      const st = String(t.status).toUpperCase();
      return st.includes("DONE") || st.includes("COMPLETE");
    }).length;
    const overdue = all.filter(
      (t) =>
        new Date(t.endDate).getTime() < Date.now() &&
        !String(t.status).toUpperCase().includes("DONE") &&
        !String(t.status).toUpperCase().includes("COMPLETE")
    ).length;
    const active = total - done;
    const progress = total ? Math.round((done / total) * 100) : 0;
    return { total, done, overdue, active, progress };
  }, [account]);

  const palette = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
  };

  /* ===================== Helpers ===================== */
  const reminderText = (t: TaskLite) => {
    if (t.remindIn === "custom")
      return t.remindAt ? `At ${handleDateWithTime(t.remindAt)}` : "Custom";
    if (t.remindIn) return `Before ${t.remindIn} (deadline)`;
    return null;
  };

  // Soft, theme-aware header background for project groups
  const projectHeaderBg = (theme: any) =>
    theme.palette.mode === "dark"
      ? `linear-gradient(90deg, ${alpha(
          theme.palette.primary.dark,
          0.28
        )} 0%, ${alpha(
          theme.palette.primary.main,
          0.18
        )} 60%, transparent 100%)`
      : `linear-gradient(90deg, ${alpha(
          theme.palette.primary.light,
          0.2
        )} 0%, ${alpha(
          theme.palette.primary.main,
          0.08
        )} 60%, transparent 100%)`;

  const expandAll = () => {
    const allIds = new Set<number>();
    projectGroups.forEach((pg) => allIds.add(pg.projectId));
    setExpandedIds(allIds);
  };
  const collapseAll = () => setExpandedIds(new Set());
  const toggleExpanded = (pid: number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(pid) ? next.delete(pid) : next.add(pid);
      return next;
    });

  const exportTasksCSV = () => {
    const flat = filteredTasks;
    if (!flat.length) return;
    const headers = [
      "id",
      "title",
      "project",
      "department",
      "status",
      "importance",
      "startDate",
      "endDate",
      "comments",
      "attachments",
    ];
    const csv = [
      headers.join(","),
      ...flat.map((t) =>
        [
          t.id,
          (t.title || "").replace(/"/g, '""'),
          t.project?.name || "",
          t.project?.department?.name || "",
          t.status,
          t.importance,
          handleDateWithTime(t.startDate),
          handleDateWithTime(t.endDate),
          t._count?.taskComments ?? 0,
          t._count?.taskAttachments ?? 0,
        ]
          .map((v) => {
            const safe = String(v).replace(/"/g, '""');
            return /[",\n]/.test(safe) ? `"${safe}"` : safe;
          })
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", `account-${account?.id}-tasks.csv`);
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 800);
  };

  const onOpenChat = async () => {
    if (!accountId) return;
    setChatLoading(true);
    try {
      const { data } = await axios.post<{ channelId: number }>(
        `${baseUrl}/chat/resolve/account/${Number(accountId)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/chat/${data.channelId}`);
    } catch (e: any) {
      setSnackbar({
        open: true,
        message:
          e?.response?.data?.message ||
          "Could not open account chat. Please check your access.",
        severity: "error",
      });
    } finally {
      setChatLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedProjects([]);
    setSelectedDepartments([]);
    setSelectedStatuses([]);
    setSelectedImportances([]);
  };

  const handleCloseSnackbar = () => setSnackbar((p) => ({ ...p, open: false }));

  /* ===================== Loading / Not found ===================== */
  if (loading) {
    return (
      <Box
        p={3}
        display="flex"
        height="100vh"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!account) {
    return (
      <Box p={3}>
        <Typography variant="h6">Account not found.</Typography>
      </Box>
    );
  }

  /* ===================== UI ===================== */
  return (
    <Box sx={{ p: 2 }}>

      {/* ======= Header + Quick Actions (mergenowd) ======= */}
      <Card
        elevation={0}
        sx={{
          mb: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          borderRadius: 2,
          background:
            theme.palette.mode === "dark"
              ? `linear-gradient(180deg, ${alpha(
                  theme.palette.background.paper,
                  0.8
                )}, ${alpha(theme.palette.background.paper, 0.6)})`
              : `linear-gradient(180deg, #fff, ${alpha("#f7f7f7", 0.9)})`,
        }}
      >
        <CardContent sx={{ py: 2 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={1.25}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              flexWrap="wrap"
            >
              <IconButton onClick={() => navigate(-1)}>
                <ArrowBackIcon sx={{ color: "#5DB0FF" }} />
              </IconButton>

              <Typography variant="h5" fontWeight={800} sx={{ mr: 1 }}>
                {account.name}
              </Typography>

              <Chip label={account.status} size="small" />
              <Chip label={account.type} size="small" />
              <Chip label={account.visibility} size="small" />

              <Tooltip title="Account info">
                <IconButton onClick={() => setInfoOpen(true)} size="small">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {account.tags?.length ? (
                <Box display="flex" gap={0.75} flexWrap="wrap" ml={0.5}>
                  {account.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              ) : null}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                variant="outlined"
                startIcon={<ForumOutlinedIcon />}
                onClick={onOpenChat}
                disabled={chatLoading}
              >
                {chatLoading ? "Opening…" : "Chat"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={exportTasksCSV}
                disabled={!(account.tasks || []).length}
              >
                Export CSV
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={fetchAccount}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* ======= Tabs ======= */}
      <Card sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab label="Overview" />
          <Tab label={`Tasks (${(account.tasks || []).length})`} />
        </Tabs>
      </Card>

      {/* ======= Overview ======= */}
      {tab === 0 && (
        <Grid container spacing={2} mb={2}>
          {/* KPI Row with progress */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {[
                {
                  title: "Total Tasks",
                  value: stats.total,
                  color: palette.primary,
                },
                {
                  title: "Completed",
                  value: stats.done,
                  color: palette.success,
                },
                { title: "Active", value: stats.active, color: palette.info },
                {
                  title: "Overdue",
                  value: stats.overdue,
                  color: palette.error,
                },
              ].map((kpi) => (
                <Grid key={kpi.title} item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    }}
                  >
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        {kpi.title}
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight={800}
                        sx={{ color: kpi.color }}
                      >
                        {kpi.value}
                      </Typography>
                      {kpi.title === "Completed" && (
                        <Box mt={1}>
                          <LinearProgress
                            variant="determinate"
                            value={stats.progress}
                            sx={{ height: 8, borderRadius: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {stats.progress}% overall
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Timeframe selector */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <TimelineIcon fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    Analytics
                  </Typography>
                </Stack>
                <ToggleButtonGroup
                  size="small"
                  value={timeframe}
                  exclusive
                  onChange={(_, v) => v && setTimeframe(v)}
                >
                  <ToggleButton value="12m">Last 12 months</ToggleButton>
                  <ToggleButton value="all">All time</ToggleButton>
                </ToggleButtonGroup>
              </CardContent>
            </Card>
          </Grid>

          {/* Charts: Monthly (wide) + Status (donut) */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                height: 380,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              }}
            >
              <CardContent sx={{ height: "100%" }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LeaderboardIcon fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={700}>
                      Tasks by month (total vs. done)
                    </Typography>
                  </Stack>
                </Stack>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ height: 300 }}>
                  {monthlyChartDataFiltered.length ? (
                    <ResponsiveContainer>
                      <AreaChart data={monthlyChartDataFiltered}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis allowDecimals={false} />
                        <RTooltip />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="total"
                          name="Total"
                          stroke={theme.palette.primary.main}
                          fill={theme.palette.primary.main}
                          fillOpacity={0.18}
                        />
                        <Area
                          type="monotone"
                          dataKey="done"
                          name="Done"
                          stroke={theme.palette.success.main}
                          fill={theme.palette.success.main}
                          fillOpacity={0.24}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box
                      sx={{
                        height: "100%",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No timeline data
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                height: 380,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              }}
            >
              <CardContent sx={{ height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700} mb={1}>
                  Status distribution
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ height: 300 }}>
                  {statusChartData.length ? (
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={statusChartData}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={60}
                          outerRadius={100}
                          label
                        >
                          {statusChartData.map((_, i) => (
                            <Cell
                              key={`cell-s-${i}`}
                              fill={
                                [
                                  palette.info,
                                  palette.success,
                                  palette.warning,
                                  palette.error,
                                  palette.secondary,
                                  palette.primary,
                                ][i % 6]
                              }
                            />
                          ))}
                        </Pie>
                        <RTooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box
                      sx={{
                        height: "100%",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No tasks
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Importance + Departments */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: 360,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              }}
            >
              <CardContent sx={{ height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700} mb={1}>
                  Importance
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ height: 280 }}>
                  {importanceChartData.length ? (
                    <ResponsiveContainer>
                      <BarChart data={importanceChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis allowDecimals={false} />
                        <RTooltip />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {importanceChartData.map((d, i) => {
                            const name = d.name.toLowerCase();
                            const fill = name.startsWith("low")
                              ? theme.palette.success.main
                              : name.startsWith("medium")
                              ? theme.palette.info.main
                              : name.startsWith("high")
                              ? theme.palette.warning.main
                              : theme.palette.error.main;
                            return <Cell key={`cell-i-${i}`} fill={fill} />;
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box
                      sx={{
                        height: "100%",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No tasks
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: 360,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              }}
            >
              <CardContent sx={{ height: "100%" }}>
                <Typography variant="subtitle1" fontWeight={700} mb={1}>
                  Departments
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ height: 280 }}>
                  {departmentChartData.length ? (
                    <ResponsiveContainer>
                      <BarChart
                        data={departmentChartData}
                        layout="vertical"
                        margin={{ left: 32 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis dataKey="name" type="category" width={120} />
                        <RTooltip />
                        <Bar
                          dataKey="value"
                          radius={[0, 4, 4, 0]}
                          fill={theme.palette.secondary.main}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box
                      sx={{
                        height: "100%",
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No department data
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ======= Tasks ======= */}
      {tab === 1 && (
        <>
          {/* Filters */}
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search"
                    placeholder="Title, description, reference…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2.5}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={projectOptions}
                    value={selectedProjects}
                    onChange={(_, v) => setSelectedProjects(v)}
                    getOptionLabel={(o) => o.name}
                    renderInput={(params) => (
                      <TextField {...params} label="Project" />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2.5}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={deptOptions}
                    value={selectedDepartments}
                    onChange={(_, v) => setSelectedDepartments(v)}
                    getOptionLabel={(o) => o.name}
                    renderInput={(params) => (
                      <TextField {...params} label="Department" />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={1.5}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={statusOptions}
                    value={selectedStatuses}
                    onChange={(_, v) => setSelectedStatuses(v)}
                    getOptionLabel={(o) => getTaskStatusLabel(o)}
                    renderInput={(params) => (
                      <TextField {...params} label="Status" />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={1.5}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={importanceOptions}
                    value={selectedImportances}
                    onChange={(_, v) => setSelectedImportances(v)}
                    getOptionLabel={(o) => getImportanceContainerText(o)}
                    renderInput={(params) => (
                      <TextField {...params} label="Importance" />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={0.5}>
                  <Box display="flex" gap={1} justifyContent="flex-end">
                    <Button size="small" onClick={clearFilters}>
                      Clear
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Header Row (counts + expanders) */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
            flexWrap="wrap"
            gap={1}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Projects ({projectGroups.length})
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" color="text.secondary">
                Showing {projectGroups.length === 0 ? 0 : startIdx + 1}–{endIdx}{" "}
                of {projectGroups.length}
              </Typography>
              <Tooltip title="Expand all">
                <IconButton onClick={expandAll}>
                  <ExpandMoreIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Collapse all">
                <IconButton onClick={collapseAll}>
                  <ExpandLessIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Project Groups */}
          <Grid container spacing={2}>
            {pagedProjectGroups.map((pg) => {
              const expanded = expandedIds.has(pg.projectId);

              const total = pg.tasks.length;
              const todo = pg.tasks.filter(
                (t) => t.status === TaskStatus.TODO
              ).length;
              const inProgress = pg.tasks.filter(
                (t) => t.status === TaskStatus.IN_PROGRESS
              ).length;
              const postponed = pg.tasks.filter(
                (t) => t.status === TaskStatus.POSTPONED
              ).length;

              // Unique assignees summary (project-level)
              const uniqueAssignees = pg.assignees.slice(0, 6);

              return (
                <Grid key={pg.projectId} item xs={12}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    }}
                  >
                    {/* Header */}
                    <Box
                      onClick={() => toggleExpanded(pg.projectId)}
                      sx={{
                        px: 1.5,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        background: projectHeaderBg(theme),
                        borderBottom: `1px solid ${alpha(
                          theme.palette.primary.main,
                          theme.palette.mode === "dark" ? 0.35 : 0.18
                        )}`,
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          minWidth: 0,
                        }}
                      >
                        <FolderIcon fontSize="small" />
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{
                            maxWidth: { xs: 220, sm: 420 },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (pg.projectId > 0)
                              navigate(`/projects/${pg.projectId}`);
                          }}
                          title={pg.projectName}
                        >
                          {pg.projectName}
                        </Typography>
                        {pg.departmentName && (
                          <Chip
                            label={pg.departmentName}
                            size="small"
                            variant="outlined"
                          />
                        )}

                        {/* Project-level assignees (summary) */}
                        {uniqueAssignees.length > 0 && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <AvatarGroup
                              max={6}
                              sx={{
                                "& .MuiAvatar-root": {
                                  width: 24,
                                  height: 24,
                                  fontSize: 12,
                                },
                              }}
                            >
                              {uniqueAssignees.map((u) => (
                                <Tooltip key={u.id} title={u.username}>
                                  <Avatar
                                    alt={u.username}
                                    src={u.profile?.imageUrl || undefined}
                                  >
                                    {u.username?.charAt(0).toUpperCase()}
                                  </Avatar>
                                </Tooltip>
                              ))}
                            </AvatarGroup>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {pg.assignees.length} assignee
                              {pg.assignees.length === 1 ? "" : "s"}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        flexWrap="wrap"
                      >
                        <Chip label={`${total} tasks`} size="small" />
                        <Chip
                          size="small"
                          label={`Todo: ${todo}`}
                          sx={{
                            backgroundColor: getTaskStatusContainerBgColor(
                              TaskStatus.TODO
                            ),
                            color: getTaskStatusContainerTextColor(
                              TaskStatus.TODO
                            ),
                          }}
                        />
                        <Chip
                          size="small"
                          label={`In progress: ${inProgress}`}
                          sx={{
                            backgroundColor: getTaskStatusContainerBgColor(
                              TaskStatus.IN_PROGRESS
                            ),
                            color: getTaskStatusContainerTextColor(
                              TaskStatus.IN_PROGRESS
                            ),
                          }}
                        />
                        <Chip
                          size="small"
                          label={`Postponed: ${postponed}`}
                          sx={{
                            backgroundColor: getTaskStatusContainerBgColor(
                              TaskStatus.POSTPONED
                            ),
                            color: getTaskStatusContainerTextColor(
                              TaskStatus.POSTPONED
                            ),
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Body */}
                    {expanded && (
                      <Box>
                        <Divider />
                        <List dense disablePadding>
                          {pg.tasks
                            .slice()
                            .sort(
                              (a, b) =>
                                new Date(b.endDate).getTime() -
                                new Date(a.endDate).getTime()
                            )
                            .map((t) => {
                              const comments = t._count?.taskComments ?? 0;
                              const attachments =
                                t._count?.taskAttachments ?? 0;
                              const remText = reminderText(t);
                              const assignees = (t.assignedUsers ?? []).map(
                                (au) => au.user
                              );

                              return (
                                <ListItem
                                  key={t.id}
                                  onDoubleClick={() =>
                                    navigate(`/task/${t.id}`)
                                  }
                                  sx={{
                                    px: 1.25,
                                    py: 1,
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.action.hover,
                                    },
                                    cursor: "pointer",
                                  }}
                                  secondaryAction={
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1.25}
                                    >
                                      <Tooltip title="Comments">
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          gap={0.5}
                                        >
                                          <CommentIcon fontSize="small" />
                                          <Typography variant="caption">
                                            {comments}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                      <Tooltip title="Attachments">
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          gap={0.5}
                                        >
                                          <AttachFileIcon fontSize="small" />
                                          <Typography variant="caption">
                                            {attachments}
                                          </Typography>
                                        </Box>
                                      </Tooltip>
                                      {remText && (
                                        <Tooltip title={remText}>
                                          <NotificationsNoneIcon
                                            fontSize="small"
                                            sx={{
                                              opacity: t.reminded ? 0.5 : 1,
                                            }}
                                          />
                                        </Tooltip>
                                      )}
                                    </Box>
                                  }
                                >
                                  <ListItemText
                                    primary={
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        flexWrap="wrap"
                                      >
                                        <Typography
                                          variant="body1"
                                          fontWeight={600}
                                        >
                                          {t.title}
                                        </Typography>
                                        <Chip
                                          size="small"
                                          label={getTaskStatusLabel(t.status)}
                                          sx={{
                                            backgroundColor:
                                              getTaskStatusContainerBgColor(
                                                t.status
                                              ),
                                            color:
                                              getTaskStatusContainerTextColor(
                                                t.status
                                              ),
                                          }}
                                        />
                                        <Chip
                                          size="small"
                                          label={getImportanceContainerText(
                                            t.importance
                                          )}
                                          sx={{
                                            backgroundColor:
                                              getImportanceContainerColor(
                                                t.importance
                                              ),
                                            color: "#fff",
                                          }}
                                        />
                                      </Box>
                                    }
                                    secondary={
                                      <Box
                                        mt={0.5}
                                        display="flex"
                                        alignItems="center"
                                        gap={2}
                                        flexWrap="wrap"
                                      >
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Start:{" "}
                                          {handleDateWithTime(t.startDate)}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Due: {handleDateWithTime(t.endDate)}
                                        </Typography>
                                        {remText && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {remText}
                                          </Typography>
                                        )}
                                        {assignees.length > 0 && (
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                          >
                                            <AvatarGroup
                                              max={4}
                                              sx={{
                                                "& .MuiAvatar-root": {
                                                  width: 22,
                                                  height: 22,
                                                  fontSize: 12,
                                                },
                                              }}
                                            >
                                              {assignees.map((u) => (
                                                <Tooltip
                                                  key={u.id}
                                                  title={u.username}
                                                >
                                                  <Avatar
                                                    alt={u.username}
                                                    src={
                                                      u.profile?.imageUrl ||
                                                      undefined
                                                    }
                                                  >
                                                    {u.username
                                                      ?.charAt(0)
                                                      .toUpperCase()}
                                                  </Avatar>
                                                </Tooltip>
                                              ))}
                                            </AvatarGroup>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              {assignees
                                                .map((u) => u.username)
                                                .join(", ")}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              );
                            })}
                        </List>
                      </Box>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <>
              <Divider sx={{ mt: 1, mb: 1 }} />
              <Box display="flex" justifyContent="center">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, p) => setPage(p)}
                  color="primary"
                  shape="rounded"
                  siblingCount={1}
                  boundaryCount={1}
                />
              </Box>
            </>
          )}
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Info Dialog */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Account Info</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <BusinessIcon fontSize="small" />
                <Typography variant="body2">Name:&nbsp;</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {account.name}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="body2">Slug:&nbsp;</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {account.slug}
                </Typography>
              </Box>
              {account.domain && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PublicIcon fontSize="small" />
                  <Typography variant="body2">Domain:&nbsp;</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {account.domain}
                  </Typography>
                </Box>
              )}
              {account.website && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <LanguageIcon fontSize="small" />
                  <Typography variant="body2">Website:&nbsp;</Typography>
                  <a href={account.website} target="_blank" rel="noreferrer">
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="primary"
                    >
                      {account.website}
                    </Typography>
                  </a>
                </Box>
              )}
              {account.email && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <EmailIcon fontSize="small" />
                  <Typography variant="body2">Email:&nbsp;</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {account.email}
                  </Typography>
                </Box>
              )}
              {account.phone && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <LocalPhoneIcon fontSize="small" />
                  <Typography variant="body2">Phone:&nbsp;</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {account.phone}
                  </Typography>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              {(account.address || account.city || account.country) && (
                <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                  <PlaceIcon fontSize="small" style={{ marginTop: 2 }} />
                  <Box>
                    <Typography variant="body2">Address</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {[
                        account.address || undefined,
                        account.city || undefined,
                        account.country || undefined,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mb={1}
                flexWrap="wrap"
              >
                <Typography variant="body2">Type:&nbsp;</Typography>
                <Chip label={account.type} size="small" />
                <Typography variant="body2" ml={2}>
                  Status:&nbsp;
                </Typography>
                <Chip label={account.status} size="small" />
                <Typography variant="body2" ml={2}>
                  Visibility:&nbsp;
                </Typography>
                <Chip label={account.visibility} size="small" />
              </Box>

              {account.description && (
                <Box mt={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Description
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {account.description}
                  </Typography>
                </Box>
              )}
              {account.notes && (
                <Box mt={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Notes
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    whiteSpace="pre-line"
                  >
                    {account.notes}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)}>Close</Button>
          <Button
            onClick={onOpenChat}
            startIcon={<ForumOutlinedIcon />}
            disabled={chatLoading}
          >
            Open Chat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountDetails;
